import React, { useState } from "react";
import Logo from "@/assets/Pitchster-logo.png";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import useAuthStore from "@/store/useAuthStore";
import { jwtDecode } from "jwt-decode";
import { LOCALSTORAGE_KEYS } from "@/config";
import { User, Lock, Eye, EyeOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useUserStore } from "@/store";
import { isValid } from "@/utils/validators";
import { useTenantContext } from "@/components/Navbar/TenantContext";
import { toast } from "react-toastify";
import TurnstileWidget from '@/components/captcha/TurnstileWidget'; 


interface JwtPayload {
  name: string;
  role: string;
  id: string;
  tenantid: string;
}

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const { loginUser, loginStatus } = useAuth();
  const navigate = useNavigate();
  const setPageHeader = useUserStore((state) => state.setPageHeader);
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  const setName = useAuthStore((state) => state.setName);
  const setCompanyName = useAuthStore((state) => state.setCompanyName);
  const setRole = useAuthStore((state) => state.setRole);
  const setId = useAuthStore((state) => state.setId);
  const setTenantId = useAuthStore((state) => state.setTenantId);
  const { setTenantName } = useTenantContext(); // ✅ ADD THIS
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);


  const [error, setError] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setError((prev) => ({
      ...prev,
      [name]:
        value.trim() === ""
          ? `${name === "email" ? "Please enter email" : "Please enter password"}`
          : "",
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      email: "",
      password: "",
    };

    if (!formData.email.trim()) {
      newErrors.email = "Valid email required";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!isValid("email", formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!isValid("password", formData.password)) {
      newErrors.password =
        "Password must include uppercase, lowercase, number, and be at least 6 characters";
    }

    if (!captchaToken) {
      toast.error("Please complete captcha verification");
      return;
    }

    setError(newErrors);

    const hasError = Object.values(newErrors).some((err) => err !== "");
    if (hasError) return;

    loginUser(    
    { ...formData, captchaToken },
    {
      onSuccess: (data) => {
        const token = data.data.token;
        if (token) {
          const decoded = jwtDecode<JwtPayload>(token);
          if (decoded.role === "USER") {
            toast.error("Unauthorized access to the admin portal.");
            return;
          }
          const refreshToken = data.data.refreshToken || token;
          localStorage.setItem("tenantId", data.data.tenantId ?? "");
          localStorage.setItem(LOCALSTORAGE_KEYS.token, token);
          localStorage.setItem(LOCALSTORAGE_KEYS.refreshToken, refreshToken);
          localStorage.setItem("auth_token", token);
          localStorage.setItem("user-store", data.data.name);

          const companyName = data?.data?.tenant?.name || "";

          localStorage.setItem(
            "Details-store",
            JSON.stringify({ companyName }),
          );

          setTenantName(companyName);

          setCompanyName(companyName);
          setIsAuthenticated(true);
          setName(data.data.name);
          setRole(decoded.role);
          setId(decoded.id);
          setTenantId(decoded.tenantid);
          if (decoded?.role === "SUPER_ADMIN") {
            setPageHeader("Super Admin Dashboard");
          }
          if (decoded?.role === "ADMIN") {
            setPageHeader("Admin Dashboard");
          }
          if (decoded?.role === "MANAGER") {
            setPageHeader("Manager");
          }
          navigate("/dashboard");
        }
      },
      onError: (error) => {
        console.error("Login failed:", error);
        toast.error(error?.message || "Login failed. Please try again.");
        setCaptchaToken(null);
      },
    });
  };

  return (
    <div className="min-h-screen flex font-sans text-white bg-gradient-to-br from-[#0e002f] via-[#1d0340] to-[#0f001f]">
      <div className="w-full md:w-1/2 flex items-center justify-center px-8 py-12 bg-[#1a103f]">
        <div className="w-full max-w-sm space-y-8">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img src={Logo} alt="Logo" className="w-40 h-15" />
          </div>

          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-4xl text-pink-500">
              <User size={40} />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="relative">
              <input
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleChange}
                placeholder="Username"
                className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {error.email && (
                <p className="text-sm text-red-500 mt-1">{error.email}</p>
              )}
              <User className="absolute top-3 left-3 text-gray-400" size={20} />
            </div>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleChange}
                placeholder="Password"
                className="w-full pl-10 pr-10 py-3 bg-white/5 rounded-lg border border-white/10 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {error.password && (
                <p className="text-sm text-red-500 mt-1">{error.password}</p>
              )}
              <Lock className="absolute top-3 left-3 text-gray-400" size={20} />

              <div
                className="absolute top-2.5 right-3 cursor-pointer group"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {showPassword ? (
                    <motion.div
                      key="eye-off"
                      initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                      transition={{ duration: 0.1 }}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 border border-pink-400 group-hover:scale-110 group-hover:shadow-pink-500/50 group-hover:shadow-md transition-all"
                    >
                      <EyeOff className="text-pink-300" size={18} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="eye"
                      initial={{ opacity: 0, rotate: 90, scale: 0.5 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: -90, scale: 0.5 }}
                      transition={{ duration: 0.1 }}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 border border-pink-400 group-hover:scale-110 group-hover:shadow-pink-500/50 group-hover:shadow-md transition-all"
                    >
                      <Eye className="text-pink-300" size={18} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="flex justify-end w-full px-1 text-sm text-gray-300">
              <a
                className="hover:underline cursor-pointer"
                onClick={() => navigate("/forgotPassword")}
              >
                Forgot password?
              </a>
            </div>

             <TurnstileWidget
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => {
                  setCaptchaToken(null);
                }}
                onError={() => {
                  setCaptchaToken(null);
                }}
              />

            <button
              type="submit"
              className="w-full py-3 bg-pink-600 hover:bg-pink-700 cursor-pointer transition rounded-lg font-semibold disabled:opacity-50"
              disabled={loginStatus === "pending"}
            >
              {loginStatus === "pending" ? "Logging in..." : "LOGIN"}
            </button>
          </form>
        </div>
      </div>

      <div className="hidden md:flex w-1/2 items-center justify-center bg-gradient-to-tr from-[#1f005c] to-[#2c005e] relative overflow-hidden">
        <div className="absolute w-[300px] h-[300px] bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 opacity-30 rounded-full blur-3xl animate-pulse"></div>
        <div className="text-center z-10 px-10">
          <h1 className="text-5xl font-bold mb-4">Welcome Admin</h1>
          <p className="text-gray-300 text-md max-w-md mx-auto">
            Explore your dashboard and manage your content efficiently. Let’s
            streamline, optimize, and lead with clarity.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
